import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, type AppLanguage } from "@/i18n";

/**
 * Language control for the signed-out pages (login, register, password reset).
 *
 * Separate from the Settings one on purpose: that control writes the choice to
 * the user's profile, and before sign-in there is no profile to write to. This
 * one only changes i18next, which persists to localStorage under `i18nextLng`
 * and is then picked up as the first entry in the detection order.
 *
 * Detection itself already works — a German browser gets German, a Swedish one
 * Swedish. What was missing was a way to *override* it: an athlete whose OS is
 * in one language but who wants the app in another had no route at all, and the
 * password-reset flow is exactly where being stuck in the wrong language hurts.
 */
const LABELS: Record<AppLanguage, string> = {
  en: "English",
  ru: "Русский",
  uk: "Українська",
  es: "Español",
  de: "Deutsch",
  fr: "Français",
  it: "Italiano",
  pt: "Português",
  sv: "Svenska",
  da: "Dansk",
};

export function AuthLanguagePicker() {
  const { i18n, t } = useTranslation();
  const current = (SUPPORTED_LANGUAGES as readonly string[]).includes(i18n.language)
    ? (i18n.language as AppLanguage)
    : "en";

  return (
    <div className="mb-6 flex justify-center">
      <label className="sr-only" htmlFor="auth-language">
        {t("settings.language", "Language")}
      </label>
      <select
        id="auth-language"
        value={current}
        onChange={(e) => void i18n.changeLanguage(e.target.value)}
        className="rounded border border-cream-border bg-cream-light px-2 py-1 text-[13px] text-taupe focus:border-navy focus:outline-none focus:ring-2 focus:ring-amber/45"
      >
        {SUPPORTED_LANGUAGES.map((lang) => (
          <option key={lang} value={lang}>
            {LABELS[lang]}
          </option>
        ))}
      </select>
    </div>
  );
}
